import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@tanstack/react-table";
import type React from "react";
import { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { FeedPanelContext } from "@/components/DriverView/ui/FeedPanelContext";
import { ActionContext } from "@/models/ActionsProvider";
import { subscribeToFeed, unsubscribeFromFeed } from "@/models/actions/clientActions";
import {
  computeNonAliasChannels,
  type NonAliasChannelRow,
  nonAliasChannelsCollections,
} from "@/models/repositories/Feed";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { actions, TelemetryContext } from "@/models/TelemetryProvider";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import { type DataTableFeatures, type DataTableInstance, dataTableFeatures } from "@/ui/dataTableFeatures";

export type ChannelsContextMenuState = {
  position: { top: number; left: number } | undefined;
  target: NonAliasChannelRow | null;
  actionDisabled: boolean;
  onClose: () => void;
  onMarkAsRead: () => void;
  onSubscribe: () => void;
  onUnsubscribe: () => void;
};

export type ChannelsTableState = {
  table: DataTableInstance<NonAliasChannelRow>;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onRowClick: (row: NonAliasChannelRow) => void;
  onRowContextMenu: (row: NonAliasChannelRow, event: React.MouseEvent) => void;
  getRowClassName: (row: NonAliasChannelRow) => string;
  contextMenu: ChannelsContextMenuState;
};

/**
 * Owns the channels-roster data and actions: the reactive channel query, the TanStack table
 * instance, row navigation, and the context-menu subscribe/unsubscribe/mark-read actions. The
 * paired {@link ChannelsTableView} renders whatever this returns.
 */
export function useChannelsTable(columns: ColumnDef<DataTableFeatures, NonAliasChannelRow>[]): ChannelsTableState {
  const { currentWorkspaceId: workspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const { accountEvent } = useContext(ActionContext);
  const { openPanel } = useContext(FeedPanelContext);
  const { trackAction } = useContext(TelemetryContext);
  const navigate = useNavigate();
  const { feedId: activeFeedId } = useParams();
  const [searchValue, setSearchValue] = useState("");
  const [contextMenuPosition, setContextMenuPosition] = useState<{ top: number; left: number } | undefined>();
  const [contextMenuTarget, setContextMenuTarget] = useState<NonAliasChannelRow | null>(null);
  const [menuActionDisabled, setMenuActionDisabled] = useState(false);

  const myMembershipId = myCurrentWorkspaceMembership?.id;
  const channelRows = useCollectionsCompute(
    nonAliasChannelsCollections,
    () =>
      workspaceId && myMembershipId
        ? computeNonAliasChannels({ workspaceId, workspaceMembershipId: myMembershipId, searchValue })
        : [],
    [workspaceId, myMembershipId, searchValue],
    `ChannelsTable:nonAliasChannels:${workspaceId}`,
  );

  const data: NonAliasChannelRow[] = useMemo(
    () =>
      channelRows.map((row) => ({
        id: row.id,
        title: row.title ?? "",
        unread: row.unread ?? false,
        subscribed: Boolean(row.subscribed),
      })),
    [channelRows],
  );

  const table = useTable({
    features: dataTableFeatures,
    data,
    columns,
    getRowId: (row) => row.id,
    // The channels list is unpaginated; see `useDriverTable` for why that has to be said explicitly.
    manualPagination: true,
    enableSorting: false,
  });

  const onRowClick = useCallback(
    (row: NonAliasChannelRow) => {
      trackAction(actions.feedNavigate(row.id));
      navigate(`/workspaces/${workspaceId}/channels/${row.id}`);
      openPanel?.();
    },
    [navigate, openPanel, workspaceId, trackAction],
  );

  const onRowContextMenu = useCallback((row: NonAliasChannelRow, event: React.MouseEvent) => {
    setContextMenuPosition({ top: event.clientY - 6, left: event.clientX + 2 });
    setContextMenuTarget(row);
  }, []);

  const onClose = useCallback(() => {
    setContextMenuPosition(undefined);
    setContextMenuTarget(null);
  }, []);

  const workspaceMembershipId = myCurrentWorkspaceMembership?.id ?? "";

  const onMarkAsRead = useCallback(() => {
    if (!accountEvent || !contextMenuTarget) return;
    accountEvent("Marked Feed as Read", { feedId: contextMenuTarget.id });
    onClose();
  }, [accountEvent, contextMenuTarget, onClose]);

  const onSubscribe = useCallback(async () => {
    if (!workspaceId || !contextMenuTarget || !workspaceMembershipId) return;
    const feedId = contextMenuTarget.id;
    setMenuActionDisabled(true);
    onClose();
    try {
      await subscribeToFeed({
        workspaceId,
        feedId,
        workspaceMembershipId,
        preference: "all",
      });
    } finally {
      setMenuActionDisabled(false);
    }
  }, [workspaceId, contextMenuTarget, workspaceMembershipId, onClose]);

  const onUnsubscribe = useCallback(async () => {
    if (!workspaceId || !contextMenuTarget || !workspaceMembershipId) return;
    const feedId = contextMenuTarget.id;
    setMenuActionDisabled(true);
    onClose();
    try {
      await unsubscribeFromFeed({
        workspaceId,
        feedId,
        workspaceMembershipId,
      });
    } finally {
      setMenuActionDisabled(false);
    }
  }, [workspaceId, contextMenuTarget, workspaceMembershipId, onClose]);

  const getRowClassName = useCallback(
    (row: NonAliasChannelRow) => (row.id === activeFeedId ? "channel-row-active" : ""),
    [activeFeedId],
  );

  return {
    table,
    searchValue,
    onSearchChange: setSearchValue,
    onRowClick,
    onRowContextMenu,
    getRowClassName,
    contextMenu: {
      position: contextMenuPosition,
      target: contextMenuTarget,
      actionDisabled: menuActionDisabled,
      onClose,
      onMarkAsRead,
      onSubscribe,
      onUnsubscribe,
    },
  };
}
